/**
 * Authentication context.
 *
 * Provides:
 *   - user:    the current signed-in user object (or null)
 *   - loading: true on initial mount while we check for an existing token
 *   - login(username_or_email, password): async — returns user, or
 *       { mfa_required, mfa_token } when TOTP is needed
 *   - loginMfa(mfa_token, code): async — completes MFA, returns user
 *   - logout(): async
 *   - refreshUser(): async, re-fetches /api/auth/me
 *
 * On mount, if there's a stored access token, we call /api/auth/me to
 * validate it and populate `user`. If that fails (expired/invalid),
 * tokens are cleared and the user is shown the login page.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  api,
  clearTokens,
  getAccessToken,
  setTokens,
} from '../api/client.js';


const AuthContext = createContext(null);


export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: if we have a token, try to hydrate the user.
  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      if (!getAccessToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await api.get('/api/auth/me');
        if (!cancelled) setUser(me);
      } catch {
        // Token invalid/expired and refresh failed — clear and continue.
        clearTokens();
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    hydrate();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username_or_email, password) => {
    const data = await api.post(
      '/api/auth/login',
      { username_or_email, password },
      { skipAuth: true },
    );

    // MFA required — hand the challenge back to the caller without
    // storing tokens or fetching /me. LoginPage handles the next step.
    if (data.mfa_required) {
      return { mfa_required: true, mfa_token: data.mfa_token };
    }

    setTokens(data);
    const me = await api.get('/api/auth/me');
    setUser(me);
    return me;
  }, []);

  const loginMfa = useCallback(async (mfa_token, code) => {
    const tokens = await api.post(
      '/api/auth/login/mfa',
      { mfa_token, code },
      { skipAuth: true },
    );
    setTokens(tokens);
    const me = await api.get('/api/auth/me');
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Ignore — we're clearing tokens regardless.
    }
    clearTokens();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await api.get('/api/auth/me');
      setUser(me);
      return me;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  const value = { user, loading, login, loginMfa, logout, refreshUser };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}


export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
